import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';

interface CustomExpressionInputProps {
  columnName: string;
  currentExpression?: string;
  onExpressionChange: (expression: string) => void;
}

export const CustomExpressionInput: React.FC<CustomExpressionInputProps> = ({
  columnName,
  currentExpression = '',
  onExpressionChange,
}) => {
  const [expression, setExpression] = useState(currentExpression);

  useEffect(() => {
    onExpressionChange(expression);
  }, [expression, onExpressionChange]);

  return (
    <div className="space-y-4">
      <div>
        <Label>Custom SQL Expression</Label>
        <Textarea
          placeholder={`Enter custom SQL expression (e.g., UPPER("${columnName}"))`}
          value={expression}
          onChange={(e) => setExpression(e.target.value)}
          rows={4}
          className="font-mono text-sm"
        />
        <div className="text-xs text-muted-foreground mt-1">
          Use double quotes for column names: &quot;{columnName}&quot;
        </div>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <div className="space-y-2">
            <div className="font-medium">Tips for custom expressions:</div>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Use double quotes for column names: &quot;column_name&quot;</li>
              <li>Use single quotes for string literals: &apos;text&apos;</li>
              <li>Combine multiple functions: UPPER(TRIM(&quot;{columnName}&quot;))</li>
              <li>Use operators: &quot;price&quot; * 1.1, &quot;name&quot; || &apos; Suffix&apos;</li>
            </ul>
          </div>
        </AlertDescription>
      </Alert>

      <div className="text-sm text-muted-foreground">
        <strong>Common Examples:</strong>
        <div className="mt-2 space-y-2">
          <div>
            <div className="font-medium">String manipulation:</div>
            <code className="block p-2 bg-muted rounded text-xs mt-1">
              UPPER(TRIM(&quot;{columnName}&quot;))
            </code>
          </div>
          <div>
            <div className="font-medium">Math with multiple columns:</div>
            <code className="block p-2 bg-muted rounded text-xs mt-1">
              &quot;quantity&quot; * &quot;price&quot; * 1.1
            </code>
          </div>
          <div>
            <div className="font-medium">Conditional logic:</div>
            <code className="block p-2 bg-muted rounded text-xs mt-1">
              CASE WHEN &quot;{columnName}&quot; &gt; 100 THEN &apos;High&apos; ELSE &apos;Low&apos; END
            </code>
          </div>
          <div>
            <div className="font-medium">Date formatting:</div>
            <code className="block p-2 bg-muted rounded text-xs mt-1">
              STRFTIME(&quot;{columnName}&quot;, &apos;%Y-%m&apos;)
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};
